import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Sparkles, Briefcase, Palette, Code, 
  GraduationCap, Video, Music, MessageSquare, Home, FileText,
  Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ThemeToggle';
import { UserMenu } from '@/components/UserMenu';
import { ScrollReveal } from '@/components/ScrollReveal';
import { BotCard } from '@/components/BotCard';
import { BotProfileModal } from '@/components/BotProfileModal';
import { SEOHead } from '@/components/SEOHead';
import { supabase } from '@/integrations/supabase/client';
import { useFavorites } from '@/hooks/useFavorites';
import { useComparisonContext } from '@/contexts/ComparisonContext';
import { Bot } from '@/types/Bot';
import { VergoLogo } from '@/components/VergoLogo';

const categoryConfig: Record<string, { 
  label: string; 
  icon: React.ComponentType<any>; 
  color: string; 
  description: string;
  keywords: string[];
}> = {
  'productivity': {
    label: 'Productivity AI Tools',
    icon: Briefcase,
    color: 'from-blue-500 to-cyan-500',
    description: 'Boost your workflow with AI-powered productivity tools. Find the best AI assistants for task management, scheduling, and business automation.',
    keywords: ['Business & Productivity', 'Utilities & Tools', 'Presentations & Documents']
  },
  'creative': {
    label: 'Creative AI Tools',
    icon: Palette,
    color: 'from-pink-500 to-rose-500',
    description: 'Unleash your creativity with AI design and creative tools. Generate images, edit photos, and create stunning visuals.',
    keywords: ['Design & Creative Tools', 'Image Editing & Generation', 'Avatar & Character']
  },
  'coding': {
    label: 'Developer AI Tools',
    icon: Code,
    color: 'from-green-500 to-emerald-500',
    description: 'Write better code faster with AI coding assistants. Debug, generate, and optimize code with powerful developer tools.',
    keywords: ['Developer Tools']
  },
  'learning': {
    label: 'Learning AI Tools',
    icon: GraduationCap,
    color: 'from-yellow-500 to-orange-500',
    description: 'Learn anything with AI tutors and educational assistants. Master new skills with personalized AI-powered learning.',
    keywords: ['Education & Learning']
  },
  'video': {
    label: 'Video AI Tools',
    icon: Video,
    color: 'from-red-500 to-pink-500',
    description: 'Create and edit videos with AI. Generate, edit, and enhance video content using cutting-edge AI technology.',
    keywords: ['Video Creation & Editing']
  },
  'audio': {
    label: 'Audio & Music AI Tools',
    icon: Music,
    color: 'from-purple-500 to-violet-500',
    description: 'Create music and audio with AI. Generate songs, edit audio, and produce professional soundtracks.',
    keywords: ['Audio & Music']
  },
  'chat': {
    label: 'AI Chat & Assistants',
    icon: MessageSquare,
    color: 'from-indigo-500 to-blue-500',
    description: 'Chat with intelligent AI assistants. Get answers, have conversations, and accomplish tasks with AI chatbots.',
    keywords: ['AI Chat & Assistants']
  },
  'design': {
    label: 'Design AI Tools',
    icon: Home,
    color: 'from-teal-500 to-cyan-500',
    description: 'Design with AI assistance. Create websites, interiors, and graphics with powerful AI design tools.',
    keywords: ['Home & Interior Design', 'Website Builders']
  },
  'writing': {
    label: 'Writing AI Tools',
    icon: FileText,
    color: 'from-amber-500 to-yellow-500',
    description: 'Write better with AI. Generate content, fix grammar, and craft compelling copy with AI writing assistants.',
    keywords: ['Content & Writing']
  },
};

const Category = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isInComparison, canAddMore, addToComparison, removeFromComparison } = useComparisonContext();

  const config = categoryId ? categoryConfig[categoryId] : null;
  const Icon = config?.icon || Sparkles;

  useEffect(() => {
    if (!categoryId || !config) {
      navigate('/');
      return;
    }
    fetchBots();
  }, [categoryId]);

  const fetchBots = async () => {
    if (!config) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bots')
        .select('*')
        .order('rating', { ascending: false });

      if (error) throw error;

      // Filter by category keywords
      const filtered = (data || []).filter(bot => 
        config.keywords.some(keyword => 
          bot.use_case?.toLowerCase().includes(keyword.toLowerCase()) ||
          bot.subcategory?.toLowerCase().includes(keyword.toLowerCase())
        )
      );

      const mappedBots: Bot[] = filtered.map(bot => ({
        id: bot.id,
        name: bot.name,
        description: bot.description,
        category: bot.use_case || bot.subcategory || 'General',
        tags: bot.tags || [],
        price: (bot.pricing as 'Free' | 'Freemium' | 'Paid') || 'Free',
        rating: bot.rating || 4.0,
        reviewCount: Math.floor(Math.random() * 1000) + 100,
        features: bot.key_features || bot.features_array || [],
        website: bot.site_url || bot.website_url || '',
        demoUrl: bot.preview_url || undefined,
        useCase: bot.use_case || '',
        modelType: bot.model_type || 'AI',
        isTrending: bot.featured || false,
        isNew: new Date(bot.created_at).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000,
        pros: bot.pros || [],
        cons: bot.cons || [],
        popularity: bot.fit_index || Math.floor(Math.random() * 30) + 70,
        imageUrl: bot.thumbnail || '/placeholder.svg',
      }));

      setBots(mappedBots);
    } catch (error) {
      console.error('Error fetching bots:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!config) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title={`${config.label} - Find the Best AI Tools | Vergo`}
        description={config.description}
        canonical={`${window.location.origin}/category/${categoryId}`}
      />

      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <VergoLogo size="lg" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </Link>
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <ScrollReveal>
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-r ${config.color} mb-6`}
            >
              <Icon className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
              {config.label}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {config.description}
            </p>
            <div className="flex items-center justify-center gap-2 mt-6">
              <Badge variant="secondary" className="text-sm">
                {bots.length} AI Tools
              </Badge>
            </div>
          </div>
        </ScrollReveal>

        {/* Results */}
        <ScrollReveal delay={0.2}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse bg-card rounded-xl h-64" />
              ))}
            </div>
          ) : bots.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No tools found</h3>
              <p className="text-muted-foreground mb-4">
                We're still adding tools to this category.
              </p>
              <Link to="/">
                <Button>Browse All Tools</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bots.map((bot, index) => (
                <motion.div
                  key={bot.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <BotCard
                    bot={bot}
                    onToggleFavorite={toggleFavorite}
                    isFavorite={isFavorite(bot.id)}
                    isInComparison={isInComparison(bot.id)}
                    canAddToComparison={canAddMore}
                    onAddToComparison={addToComparison}
                    onRemoveFromComparison={removeFromComparison}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </ScrollReveal>

        {/* Related Categories */}
        <ScrollReveal delay={0.3}>
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Explore Other Categories
            </h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(categoryConfig)
                .filter(([id]) => id !== categoryId)
                .map(([id, cat]) => {
                  const CatIcon = cat.icon;
                  return (
                    <Link key={id} to={`/category/${id}`}>
                      <Button variant="outline" className="gap-2">
                        <CatIcon className="w-4 h-4" />
                        {cat.label.replace(' AI Tools', '')}
                      </Button>
                    </Link>
                  );
                })}
            </div>
          </section>
        </ScrollReveal>
      </main>

      {/* Bot Modal */}
      <BotProfileModal
        bot={selectedBot}
        isOpen={!!selectedBot}
        onClose={() => setSelectedBot(null)}
        onToggleFavorite={toggleFavorite}
        isFavorite={selectedBot ? isFavorite(selectedBot.id) : false}
      />

      {/* Footer */}
      <footer className="bg-card/50 border-t border-border py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© 2024 Vergo. Find your perfect AI companion.</p>
        </div>
      </footer>
    </div>
  );
};

export default Category;
